import { useController, useFormContext } from 'react-hook-form';
import { getBillingAccountListItems } from '../../../components/AddOrSelectBillingAccountsV2/AddOrSelectBillingAccounts.js';
import { AutoComplete } from '../../../components/AutoComplete/AutoComplete.js';
import { billingAccountMsg, defaultBillingAccountMsg, noSelectionMsg, selectMsg, t } from '../../i18n/index.js';
import { CREATE_NEW_BILLING_ACCOUNT_OPTION_VALUE } from '../../utils/billingAccountUtils';
import { getTextError } from '../utils.js';
import type { BasicAutoCompleteItem } from '../../../components/AutoComplete/AutoComplete.js';
import type { BillingAccountSearchResponse } from '../../../generated/api/billingAccountSearchResponse.js';

interface BillingAccountProps {
  billingAccounts: Array<BillingAccountSearchResponse>;
  name: string;
  enableAddNewBa?: boolean;
  required?: boolean;
  hideLabel?: boolean;
  onCreateNewBillingAccountSelected?: () => void;
  onChange?: (billingAccountId: string) => void;
  isDefault?: boolean;
}

export const BillingAccount = ({
  name,
  billingAccounts,
  enableAddNewBa = true,
  required = true,
  hideLabel = false,
  onCreateNewBillingAccountSelected,
  onChange,
  isDefault = false,
}: BillingAccountProps) => {
  const formattedBas = getBillingAccountListItems(enableAddNewBa, billingAccounts, '').map(x => ({
    ...x,
    id: x.value,
  }));
  const methods = useFormContext();
  const { control, setValue, setError, watch } = methods;
  const reset = watch('key') || 0;
  const rules = { required: { value: required, message: t.GMD4('The billing account must be chosen') } };
  const { field, fieldState, formState } = useController({ name, control, rules });

  // If not required, anyway check the formState for an error
  const error = required
    ? getTextError(formState.isSubmitted, fieldState.isTouched, fieldState.error)
    : formState.errors.billingAccountId?.message?.toString();

  const keys = name.split('.');
  const value =
    field.value ?? keys.reduce((current, key) => (current ? current[key] : undefined), formState.defaultValues);
  // defaultValue is the default selected value of the component if the reset changes the defaultValue is rendered according the value of the input
  const defaultValue = formattedBas.find(i => (reset === 0 ? i.value === value : i.value === field.value));

  return (
    <AutoComplete<BasicAutoCompleteItem>
      className="large-min-height"
      inputRef={field.ref}
      options={formattedBas}
      defaultOption={defaultValue}
      getUniqueId={i => i.value}
      getDisplayHtml={i => i.html}
      getDisplayValue={i => i.label}
      onInputBlur={field.onBlur}
      onInputChange={(e, item) => {
        setValue(name, item.value);
        setError(name, { message: undefined });
        if (item.value) {
          onChange?.(item.value);
        }
        if (item.value === CREATE_NEW_BILLING_ACCOUNT_OPTION_VALUE) {
          onCreateNewBillingAccountSelected && onCreateNewBillingAccountSelected();
        }
      }}
      config={{
        isSortable: false,
        isClearable: false,
      }}
      id={`${name}-autocomplete`}
      noOptionsMsg={t.ZW5W(noSelectionMsg)}
      label={!hideLabel ? (isDefault ? t.EYPA(defaultBillingAccountMsg) : t.IFT9(billingAccountMsg)) : undefined}
      placeholder={t.QRYV(selectMsg)}
      error={error}
      key={`${name}-autocomplete${reset}`}
    />
  );
};
